/**
 * Verrou « une commande à la fois » — agent administratif (révision initiale G1/P2)
 * et opérateurs production (phase atelier in_progress + responsible_user_id).
 *
 * Source de vérité : RPC Supabase `get_active_case_assignment` / `assign_case_operator_revision`.
 * Libération : automatique quand la phase passe à completed / cancelled.
 */

import { ADMINISTRATIVE_AGENT_ROLE_TYPE } from './phaseOperatorAssignmentRoles';
import {
  isOperatorFamilyRole,
  isSinglePhaseWorkshopStation,
  SHIPPING_STATION_ROLE_TYPE,
} from './operatorRoles';

/** @typedef {{ caseId: string, caseNumber: string|null, phaseId: string|null, phaseName: string|null, context: string }} ActiveCaseAssignment */

export class AssignmentLockConflictError extends Error {
  /**
   * @param {string} message
   * @param {ActiveCaseAssignment|null} activeAssignment
   */
  constructor(message, activeAssignment = null) {
    super(message);
    this.name = 'AssignmentLockConflictError';
    this.activeAssignment = activeAssignment;
  }
}

const LOCKABLE_ROLE_TYPES = new Set([
  ADMINISTRATIVE_AGENT_ROLE_TYPE,
  'operator_3d',
  'operator_thermo',
  'operator_finition',
  'operator_qualite',
  'operators',
  SHIPPING_STATION_ROLE_TYPE,
]);

/**
 * Rôles soumis au verrou une commande à la fois.
 * @param {string | string[] | null | undefined} roleOrRoles
 */
export function isLockableAssigneeRole(roleOrRoles) {
  const list = normalizeRoleList(roleOrRoles);
  return list.some((r) => LOCKABLE_ROLE_TYPES.has(r));
}

/**
 * @param {string | string[] | null | undefined} roleOrRoles
 */
export function isAdministratorActor(roleOrRoles) {
  return normalizeRoleList(roleOrRoles).includes('administrator');
}

function normalizeRoleList(roleOrRoles) {
  if (Array.isArray(roleOrRoles)) {
    return roleOrRoles.filter((r) => typeof r === 'string' && r.trim());
  }
  if (typeof roleOrRoles === 'string' && roleOrRoles.trim()) return [roleOrRoles];
  return [];
}

/**
 * Contexte RPC pour filtrer l'assignation active.
 * @param {string | string[] | null | undefined} roleOrRoles
 * @returns {'initial_review'|'workshop'|'any'}
 */
export function resolveAssignmentLockContext(roleOrRoles) {
  const roles = normalizeRoleList(roleOrRoles);
  if (roles.includes(ADMINISTRATIVE_AGENT_ROLE_TYPE)) return 'initial_review';
  if (roles.some((r) => isSinglePhaseWorkshopStation(r) || isOperatorFamilyRole(r))) {
    return 'workshop';
  }
  return 'any';
}

/**
 * @param {unknown} row
 * @returns {ActiveCaseAssignment|null}
 */
export function parseActiveAssignmentRpc(row) {
  if (!row || typeof row !== 'object') return null;
  const caseId = row.case_id || row.caseId;
  const hasActive =
    row.has_active === true ||
    row.hasActive === true ||
    Boolean(caseId);
  if (!hasActive || !caseId) return null;
  return {
    caseId: String(caseId),
    caseNumber: row.case_number ?? row.caseNumber ?? null,
    phaseId: row.phase_id ?? row.phaseId ?? null,
    phaseName: row.phase_name ?? row.phaseName ?? null,
    context: row.context ?? 'any',
  };
}

/**
 * Verrou « une commande à la fois » : autre dossier déjà actif pour cet agent.
 * @param {ActiveCaseAssignment|null} active
 * @param {string|null|undefined} currentCaseId
 */
export function isSelfAssignBlockedForCase(active, currentCaseId) {
  if (!active?.caseId || !currentCaseId) return Boolean(active?.caseId);
  return active.caseId !== String(currentCaseId);
}

/** Libellé bouton / infobulle lorsque l’auto-assignation est bloquée. */
export function getSelfAssignLockHint(active) {
  if (!active) return null;
  const label = active.caseNumber || active.caseId;
  return `Vous avez déjà une commande en cours (${label}). Terminez la révision initiale avant d'en prendre une autre.`;
}

/**
 * @param {import('@supabase/supabase-js').SupabaseClient} supabaseClient
 * @param {string} userId
 * @param {string} [context='any']
 */
export async function getActiveCaseAssignment(supabaseClient, userId, context = 'any') {
  if (!supabaseClient || !userId) return null;
  const { data, error } = await supabaseClient.rpc('get_active_case_assignment', {
    p_user_id: userId,
    p_context: context,
  });
  if (error) {
    console.error('[caseAssignmentLock] get_active_case_assignment', error);
    const msg = String(error.message || '');
    if (
      error.code === 'PGRST202' ||
      msg.includes('Could not find the function') ||
      msg.includes('get_active_case_assignment')
    ) {
      throw new Error(
        "Fonction Supabase « get_active_case_assignment » absente. Appliquez les migrations " +
          '20260511180000_case_assignment_lock.sql et 20260603120000_case_assignment_lock_repair.sql ' +
          'sur votre projet (Supabase SQL Editor ou supabase db push), puis rechargez le schéma API.',
      );
    }
    throw error;
  }
  return parseActiveAssignmentRpc(data);
}

/**
 * @param {ActiveCaseAssignment|null} active
 */
export function formatAssignmentLockMessage(active) {
  if (!active) return 'Cet opérateur a déjà une commande en cours.';
  const label = active.caseNumber || active.caseId;
  return `Une commande est déjà en cours (${label}). Terminez-la ou demandez à un administrateur de forcer l'assignation.`;
}

/**
 * @param {import('@supabase/supabase-js').SupabaseClient} supabaseClient
 * @param {{
 *   targetUserId: string,
 *   caseId: string,
 *   targetRoleTypes?: string | string[],
 *   actorRoleTypes?: string | string[],
 *   force?: boolean,
 * }} opts
 */
export async function assertCanAssignToUser(supabaseClient, opts) {
  const {
    targetUserId,
    caseId,
    targetRoleTypes = [],
    actorRoleTypes = [],
    force = false,
  } = opts;

  if (!targetUserId || !caseId) return;

  if (!isLockableAssigneeRole(targetRoleTypes)) return;

  const context = resolveAssignmentLockContext(targetRoleTypes);
  const active = await getActiveCaseAssignment(supabaseClient, targetUserId, context);

  if (!active) return;
  if (active.caseId === caseId) return;

  if (force && isAdministratorActor(actorRoleTypes)) return;

  throw new AssignmentLockConflictError(formatAssignmentLockMessage(active), active);
}

/**
 * Assignation révision initiale (G1/P2) via RPC transactionnelle.
 * @param {import('@supabase/supabase-js').SupabaseClient} supabaseClient
 * @param {{
 *   caseId: string,
 *   userId: string,
 *   milestoneId: string,
 *   force?: boolean,
 *   actorRoleTypes?: string | string[],
 *   targetRoleTypes?: string | string[],
 * }} params
 */
export async function assignCaseOperatorRevisionRpc(supabaseClient, params) {
  const {
    caseId,
    userId,
    milestoneId,
    force = false,
    actorRoleTypes = [],
    targetRoleTypes = [ADMINISTRATIVE_AGENT_ROLE_TYPE],
  } = params;

  await assertCanAssignToUser(supabaseClient, {
    targetUserId: userId,
    caseId,
    targetRoleTypes,
    actorRoleTypes,
    force,
  });

  const { data, error } = await supabaseClient.rpc('assign_case_operator_revision', {
    p_case_id: caseId,
    p_user_id: userId,
    p_milestone_id: milestoneId,
    p_force: force,
  });

  if (error) throw error;

  const result = data && typeof data === 'object' ? data : {};
  if (result.ok === false) {
    if (result.error === 'assignment_locked') {
      const active = parseActiveAssignmentRpc(result.active ?? result);
      throw new AssignmentLockConflictError(
        formatAssignmentLockMessage(active),
        active
      );
    }
    throw new Error(result.message || result.error || 'Erreur assignation');
  }

  return result;
}

/**
 * Vérifie le verrou avant assignation phase atelier (responsible_user_id sur phase.id).
 * @param {import('@supabase/supabase-js').SupabaseClient} supabaseClient
 * @param {{
 *   targetUserId: string,
 *   caseId: string,
 *   targetRoleTypes?: string | string[],
 *   actorRoleTypes?: string | string[],
 *   force?: boolean,
 * }} opts
 */
export async function assertCanAssignWorkshopPhase(supabaseClient, opts) {
  return assertCanAssignToUser(supabaseClient, {
    ...opts,
    targetRoleTypes: opts.targetRoleTypes?.length
      ? opts.targetRoleTypes
      : ['operator_3d', 'operator_thermo', 'operator_finition', 'operator_qualite', SHIPPING_STATION_ROLE_TYPE, 'operators'],
  });
}

export async function assignWorkshopPhaseResponsibleRpc(supabaseClient, params) {
  const {
    phaseId,
    userId,
    caseId,
    force = false,
    actorRoleTypes = [],
    targetRoleTypes = [],
  } = params;

  if (isLockableAssigneeRole(targetRoleTypes)) {
    await assertCanAssignWorkshopPhase(supabaseClient, {
      targetUserId: userId,
      caseId,
      targetRoleTypes,
      actorRoleTypes,
      force,
    });
  }

  const { data, error } = await supabaseClient.rpc('assign_workshop_phase_responsible', {
    p_phase_id: phaseId,
    p_user_id: userId,
    p_case_id: caseId,
    p_force: force,
  });

  if (error) throw error;

  const result = data && typeof data === 'object' ? data : {};
  if (result.ok === false) {
    if (result.error === 'assignment_locked') {
      const active = parseActiveAssignmentRpc(result.active ?? result);
      throw new AssignmentLockConflictError(formatAssignmentLockMessage(active), active);
    }
    throw new Error(result.message || result.error || 'Erreur assignation phase');
  }

  return result;
}

/**
 * Affiche un toast avec action optionnelle vers le cas actif.
 * @param {import('sonner').toast} toastFn
 * @param {import('react-router-dom').NavigateFunction} navigate
 * @param {AssignmentLockConflictError} err
 */
export function toastAssignmentLockConflict(toastFn, navigate, err) {
  const active = err.activeAssignment;
  const message = err.message || formatAssignmentLockMessage(active);
  if (active?.caseId && navigate) {
    toastFn.error(message, {
      action: {
        label: 'Voir la commande',
        onClick: () => navigate(`/case-detail/${active.caseId}`),
      },
    });
    return;
  }
  toastFn.error(message);
}
